import React from 'react';
import { Tag } from 'antd';
import { RobotOutlined, CheckCircleOutlined } from '@ant-design/icons';
import AiAgreementIndicator from './AiAgreementIndicator';
import { formatDdMmYyyy } from '../utils/trainingModel';

/**
 * Commandment IV: an AI-sourced value carries an explicit "needs a human" chip until a human
 * approval event exists, and the AI's agreement score is always visible next to it.
 *
 * The score is rendered by the shared `AiAgreementIndicator` — run-agreement, neutral (never a
 * colour tier), always disclaimed that it does not indicate correctness. `confidence` semantics:
 *   number    : the backend's measured agreement (0..1)
 *   null      : the wrapper ran but could not measure — stated plainly by the indicator
 *   undefined : the endpoint did not return the field at all — stated as such, not as "null"
 *
 * Renders nothing for human-authored content.
 *
 * Props: sourceType ('human' | 'ai_generated'), confidence, reviewed (a human approval exists),
 *        reviewedAt (ISO | null), draftLabel (chip text while unapproved).
 */
const AiDraftBadge = ({ sourceType, confidence, reviewed, reviewedAt, draftLabel = 'AI drafted - review' }) => {
    if (sourceType !== 'ai_generated') return null;
    return (
        <div data-testid="ai-draft-badge" style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {reviewed ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                    AI drafted - approved{reviewedAt ? ` ${formatDdMmYyyy(reviewedAt)}` : ''}
                </Tag>
            ) : (
                <Tag color="orange" icon={<RobotOutlined />}>
                    {draftLabel}
                </Tag>
            )}
            <AiAgreementIndicator
                confidence={confidence}
                unavailableReason={confidence === undefined ? 'not returned by this endpoint' : undefined}
            />
        </div>
    );
};

export default AiDraftBadge;
